import { useCallback, useEffect, useMemo, useState } from 'react';
import { useData } from '../contexts/DataContext';
import { groupUnitsByBranch } from '../utils/branchView';

/**
 * The Branches page's data, shared by today's page and the redesigned one:
 *
 * - the inventory loads on mount (a no-op when it is already in context, e.g.
 *   coming from the inventory page);
 * - each view loads its own payload lazily, the first time it is shown:
 *   'overview' → /combined and /movements, 'coverage' → /pricing-matrix, 'category' →
 *   /category-trends with the current analysis window (all cached in
 *   DataContext across routes);
 * - the analysis window lives here, so it survives view switches but not a
 *   route change; applying it reloads the trends with date_from / date_to;
 * - retry functions force a reload of one payload.
 */
export default function useBranchesData(view) {
    const data = useData();
    const {
        inventoryData, loadInventory, loadCombined, loadMatrix, loadTrends, setTrendsData, loadMovements,
    } = data;

    const [trendDateFrom, setTrendDateFrom] = useState('');
    const [trendDateTo, setTrendDateTo] = useState('');

    useEffect(() => { loadInventory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (view === 'overview') {
            loadCombined();
            // Optional: absent in contexts (and test doubles) without movements.
            if (loadMovements) loadMovements();
        }
        if (view === 'coverage') loadMatrix();
        if (view === 'category') loadTrends({ dateFrom: trendDateFrom || undefined, dateTo: trendDateTo || undefined });
    }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

    const invUnits = useMemo(() => inventoryData?.units || [], [inventoryData]);
    const invByBranch = useMemo(() => groupUnitsByBranch(invUnits), [invUnits]);

    const applyTrendWindow = useCallback((from, to) => {
        setTrendDateFrom(from);
        setTrendDateTo(to);
        setTrendsData(null);
        loadTrends({ dateFrom: from || undefined, dateTo: to || undefined, force: true });
    }, [loadTrends, setTrendsData]);

    const retryTrends = useCallback(() => {
        loadTrends({ dateFrom: trendDateFrom || undefined, dateTo: trendDateTo || undefined, force: true });
    }, [loadTrends, trendDateFrom, trendDateTo]);

    return {
        ...data,
        invUnits,
        invByBranch,
        inventoryLoaded: Boolean(inventoryData),
        trendDateFrom,
        trendDateTo,
        applyTrendWindow,
        retryCombined: useCallback(() => loadCombined(true), [loadCombined]),
        retryMatrix: useCallback(() => loadMatrix(true), [loadMatrix]),
        retryMovements: useCallback(() => { if (loadMovements) loadMovements(true); }, [loadMovements]),
        retryInventory: useCallback(() => loadInventory(true), [loadInventory]),
        retryTrends,
    };
}
